import React, {
	useState,
	useEffect,
	useContext
} from 'react';

import {
	Routes,
	useNavigate
} from 'react-router-dom';

import {
	Box,
	Button,
	CircularProgress,
	IconButton,
	Tooltip,
	Typography,
	TableCell,
	TableRow,

} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadTwoToneIcon from '@mui/icons-material/DownloadTwoTone';
import AutoModeIcon from '@mui/icons-material/AutoMode';

import { BASEPATH, DATA_SOURCES } from '../config/api';
import Footer from '../components/footer';
import ModalComponent from '../components/common/global-component/modal/modal-component';
import DialogComponent from '../components/common/global-component/dialog/dialog-component';
import ModalService from '../services/modal-service';
import { AppPermissionsContext } from '../components/common/app-context/app-permission-context';
import { getDataSources, getClients, getDataSourcesXML, getDataSourceOriginalFile, getValidationMessage } from '../services/api-services';
import TableContent from '../components/common/pages/table-content-components';
import { cbcrReportId, pillarTwoReportId } from '../config/constants';
import * as ROUTES from '../config/routes';
import axios from 'axios';

const DataSource = ({ templateId = cbcrReportId }) => {
	const { storedUser, permissions, decoded } = useContext(AppPermissionsContext);
	const [reportList, setReportList] = useState([]);
	const [objects, setObjects] = useState([]);
	const [clients, setClients] = useState([]);
	const [customHtml, setCustomHtml] = useState();
	const [downloadingIds, setDownloadingIds] = useState(new Set());

	const setDownloading = (id, busy) => setDownloadingIds(prev => {
		const next = new Set(prev);
		busy ? next.add(id) : next.delete(id);
		return next;
	});

	const templateConfigs = {
		[cbcrReportId]: {
			name: 'CbCR',
			title: 'CbCR Data Sources',
			description: 'Information to be uploaded for CbCR data analytics and visuals',
			fileType: 'CbCR'
		},
		[pillarTwoReportId]: {
			name: 'Pillar Two',
			title: 'Pillar Two Data Sources',
			description: 'Information to be uploaded for Pillar Two data analytics and visuals',
			fileType: 'PillarTwo'
		}
	};

	const templateConfig = templateConfigs[templateId];

	const screen = {
		type: `${templateConfig.name} Data Sources`,
		heading: {
			title: templateConfig.title,
			description: templateConfig.description
		},
		search: {
			type: 'text',
			placeholder: 'Search by client.',
			fullWidth: true,
			size: 'small'
		},
		tableHead: [
			'Company',
			'Period',
			'File',
			...(permissions && permissions['Data Sources'] && permissions['Data Sources'].viewIds ? ['Id'] : []),
			'XML',
			''
		]
	}
	const navigate = useNavigate();

	const formControls = [
		{
			label: "Client Name",
			placeholder: "Your client name, please",
			helperText: "Client name is required",
			required: true,
			validation: (obj) => obj
		},
		{
			label: "Company",
			placeholder: "Select your company, please",
			name: "companyId",
			type: 'selectbox',
			options: clients,
			required: true
		},
		{
			label: "Period",
			placeholder: "Select your period, please",
			name: "year",
			helperText: "Period is required",
			required: true,
			validation: (obj) => obj
		},
		{
			placeholder: "Select your data file",
			type: 'file',
			options: objects,
			name: "file",
		}
	]

	const downloadFormControls = [
		{
			label: "Surname",
			placeholder: "Enter your surname",
			name: "surname",
			required: true,
			validation: (obj) => obj
		},
		{
			label: "First Names",
			placeholder: "Enter your first names",
			name: "firstNames",
			required: true
		},
		{
			label: "Business Telephone Number 1",
			placeholder: "Enter your business telephone number 1",
			name: "busTelNo1",
			required: true
		},
		{
			label: "Business Telephone Number 2",
			placeholder: "Enter your business telephone number 2",
			name: "busTelNo2"
		},
		{
			label: "Cellphone Number",
			placeholder: "Enter your cellphone number",
			name: "cellNo",
			required: true
		},
		{
			label: "Email Address",
			placeholder: "Enter your email address",
			name: "emailAddress",
			type: "email",
			required: true
		}
	]

	const createModal = {
		form: {
			formControls,
			processText: 'Add data source',
			title: 'Create New Data Source',
			validation: () => true,
			type: 'create',
		},
		request: {
			method: 'POST',
			requestValidation: () => true,
			url: () => `${DATA_SOURCES}/create`,
			requestOverideMap: (formData) => {
				console.log('Uploaded file:', formData.file);
				const form = new FormData();
				form.append('name', formData.name ?? formData.file?.name);
				form.append('xml', formData.file?.type === 'text/xml' ? true : false);
				form.append('year', formData.year);
				form.append('company', formData.companyid);
				form.append('templateId', templateId);
				form.append('file', formData.file);

				return form;
			}
		}
	}

	const editModal = {
		form: {
			description: 'Configure your console settings',
			formControls,
			processText: 'Edit existing client',
			title: 'Edit Existing Client',
			validation: () => true,
			type: 'edit'
		},
		request: {
			method: 'PATCH',
			requestValidation: () => modals.edit.modalData.selected,
			url: () => `${DATA_SOURCES}/${modals.edit.modalData.selected.id}`,
			requestOverideMap: (data) => {
				const { templateId, ...rest } = data;
				return rest;
			}
		}
	}

	const deleteModal = {
		form: {
			description: 'Are you sure, you know what you doing?',
			formControls: [
				{
					label: "Name",
					type: 'readonly',
					disabled: true,
				},
				{
					label: "ID",
					type: 'readonly',
					disabled: true,
				}
			],
			processText: 'Delete existing client',
			title: 'Delete Existing Client',
			validation: () => true,
			type: 'delete'
		},
		request: {
			method: 'DELETE',
			requestValidation: () => modals.delete.modalData.selected.id,
			url: () => `${DATA_SOURCES}/${modals.delete.modalData.selected.id}`,
		}
	};

	const downloadModal = {
		form: {
			formControls: downloadFormControls,
			processText: 'Download file',
			title: 'Download Information',
			validation: () => true,
			type: 'download',
			customHtml
		},
		request: {
			method: 'PATCH',
			requestValidation: () => modals.download.modalData.selected.id,
			url: () => `${DATA_SOURCES}/${modals.download.modalData.selected.id}`,
			requestOverideMap: (data) => {
				const { templateId, ...rest } = data;
				return rest;
			}
		},
	};

	const modals = {
		create: ModalService(createModal),
		edit: ModalService(editModal),
		delete: ModalService(deleteModal),
		download: ModalService(downloadModal),
	}

	useEffect(() => {
		getClients(storedUser)
			.then(response => setClients(response.data));
	}, []);

	useEffect(() => {
		if (modals.download.result) {
			handleDownload(modals.download.selected.id, modals.download.selected.name);
		}
	}, [modals.download.result]);

	useEffect(() => {
		getDataSources(storedUser, templateId)
			.then(response => {
				if (response?.data) {
					response.data = response.data
						// .filter(f => f.templateId === templateId)
						.map(f => ({ ...f, companyName: f.client?.name || 'Unknown Client' }));
					setObjects(response.data);
				}
			})
			.catch(error => {
				console.error('Error loading data sources:', error);
				setObjects([]);
			});
	}, [clients, modals.delete.result, modals.edit.result, modals.create.result, modals.download.result]);

	useEffect(() => {
		if (modals.create?.resultData?.id) generateXML(modals.create.resultData.id);
	}, [modals.create.resultData]);

	const generateXML = async (id) => {
		setDownloading(id, true);
		try {
			const response = await getDataSources(storedUser, templateId);
			response.data = response.data.map(f => ({ ...f, companyName: f.client.name }));
			const dataSource = response.data.find(obj => obj.id === id);

			if (!dataSource) {
				console.warn('Datasource not found');
				return;
			}

			const validationMessage = await getValidationMessage(storedUser, id);
			downloadFile(validationMessage.data);

			setObjects(response.data);
		} catch (error) {
			console.error('Error generating XML:', error);
			alert('An error occurred while generating XML. Please try again.');
		} finally {
			setDownloading(id, false);
		}
	};

	const actions = [
		{
			icon: <AddIcon />,
			name: 'Add New Console',
			callback: modals.create.open
		}
	];

	const filterOptions = (obj, _, searchValue) => {
		return obj && (obj.name && obj.name.toLowerCase().includes(searchValue.toLowerCase()) ||
			obj.client && obj.client.name && obj.client.name.toLowerCase().includes(searchValue.toLowerCase()) ||
			obj.templateName && obj.templateName.toLowerCase().includes(searchValue.toLowerCase()) ||
			obj.year && obj.year.toLowerCase().includes(searchValue.toLowerCase()))
	}

	const triggerBlobDownload = (blob, filename) => {
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.setAttribute('download', filename);
		document.body.appendChild(link);
		link.click();
		URL.revokeObjectURL(url);
		document.body.removeChild(link);
	}

	const handleDownload = (id, file) => {
		setDownloading(id, true);
		const isPillarTwo = templateId === pillarTwoReportId;
		const downloadPromise = isPillarTwo
			? getDataSourceOriginalFile(storedUser, id).then(response => triggerBlobDownload(response.data, file))
			: getDataSourcesXML(storedUser, id).then(response => triggerBlobDownload(response.data, `${file.replace('.xlsm', '')}.xml`));
		downloadPromise
			.catch(error => {
				console.error('Error downloading file: ', error);
				alert('An error occurred while downloading the file. Please try again.');
			})
			.finally(() => setDownloading(id, false));
	}

	const tableBodyMapper = (f) => (
		<TableRow key={`${f.id}_row`} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
			<TableCell key={`${f.id}_company`}>{f.companyName}</TableCell>
			<TableCell key={`${f.id}_period`}>{f.year}</TableCell>
			<TableCell key={`${f.id}_file`}>{f.file}</TableCell>
			{permissions && permissions['Data Sources'] && permissions['Data Sources'].viewIds && <TableCell key={`${f.id}_id`}>{f.id}</TableCell>}
			<TableCell key={`${f.id}_xml`}>{f.xml ? 'Yes' : 'No'}</TableCell>

			<TableCell key={`${f.id}_actions`}>
				<Typography key={`${f.id}_typo`}>
					{permissions && permissions['Data Sources'] && permissions['Data Sources'].read && <Tooltip title="View data source" arrow><IconButton key={`${f.id}_show`}
						onClick={(e) => navigate(`${ROUTES.DATA_SOURCE_SHOW}/${f.id}`)}>
						<VisibilityIcon />
					</IconButton></Tooltip>
					}
					{permissions && permissions['Data Sources'] && permissions['Data Sources'].update && false && <Tooltip title="Edit data source" arrow><IconButton key={`${f.id}_edit`}
						onClick={(e) => modals.edit.open(e, f)}>
						<EditTwoToneIcon />
					</IconButton></Tooltip>
					}
					{permissions && permissions['Data Sources'] && permissions['Data Sources'].delete && <Tooltip title="Delete data source" arrow><IconButton key={`${f.id}_delete`}
						onClick={(e) => modals.delete.open(e, f)}>
						<DeleteTwoToneIcon />
					</IconButton></Tooltip>
					}
					{permissions && permissions['Data Sources'] && permissions['Data Sources'].download && !f.xml && templateId !== pillarTwoReportId && <Tooltip title="Generate validation report (.pdf)" arrow><IconButton key={`${f.id}_download_regen`}
						disabled={downloadingIds.has(f.id)}
						onClick={(e) => generateXML(f.id)}>
						{downloadingIds.has(f.id) ? <CircularProgress size={20} /> : <AutoModeIcon />}
					</IconButton></Tooltip>
					}
					{permissions && permissions['Data Sources'] && permissions['Data Sources'].download && <Tooltip title={templateId === pillarTwoReportId ? 'Download Excel' : 'Download XML'} arrow><IconButton key={`${f.id}_download`}
						disabled={downloadingIds.has(f.id)}
						onClick={(e) => templateId === pillarTwoReportId ? handleDownload(f.id, f.file || f.name || 'download') : modals.download.open(e, f)}>
						{downloadingIds.has(f.id) ? <CircularProgress size={20} /> : <DownloadTwoToneIcon />}
					</IconButton></Tooltip>
					}
				</Typography>
			</TableCell>
		</TableRow>
	);

	const downloadFile = (blob) => triggerBlobDownload(blob, 'file.pdf');

	const getModalComponent = (modalData) => {
		const validation = modalData && modalData.validation && modalData.validation();
		return (validation && <ModalComponent key={modalData.type} data={modalData}>
			<Box>
				{
					((customHtml && <Button onClick={(e) => downloadFile(e)}>test</Button>) ||
						<form noValidate autoComplete='off'>
							{
								<DialogComponent key={modalData.type} data={{ ...modalData, defaultValue: modalData.selected }}></DialogComponent>
							}
						</form>)
				}
			</Box>
		</ModalComponent>
		)
	};

	return (
		<React.Fragment>
			<TableContent data={{
				area: 'Data Sources',
				objects,
				body: tableBodyMapper,
				filterOptions,
				screen
			}}>
				<Footer actions={permissions.Users.create && actions} />
				{
					[
						getModalComponent(modals.create.modalData),
						getModalComponent(modals.edit.modalData),
						getModalComponent(modals.delete.modalData),
						getModalComponent(modals.download.modalData)
					]
				}
			</TableContent>
		</React.Fragment>
	);
}

export default DataSource;